<template>
  <div>
    <div v-if="stockRecord && available" class="pricing-detail">
      <table>
        <tbody>
          <tr>
            <td colspan="3">
              <RetailPrice
                v-if="stockRecord.show_retail_price"
                :amt="Number(object.retail_price)"
              />
            </td>
          </tr>
          <tr class="current">
            <td class="price">
              <Price :amt="Number(stockRecord.price_excl_tax)" />
            </td>
            <td
              v-show="
                stockRecord.show_retail_price &&
                !isTinyDiscount(object, stockRecord.price_excl_tax)
              "
              class="savings"
            >
              {{
                calculatePercentageDiscount(object, stockRecord.price_excl_tax)
              }}% off
            </td>
            <td v-if="stockRecord.discount_threshold" class="count">
              1-{{ stockRecord.discount_threshold - 1 }} bottles
              {{ itemSizeStr }}
            </td>
            <td v-else class="count">per {{ getItemType }}</td>
          </tr>
          <tr v-if="stockRecord.discount_price_excl_tax">
            <td class="price">
              <Price :amt="Number(stockRecord.discount_price_excl_tax)" />
            </td>
            <td
              v-show="
                !isTinyDiscount(object, stockRecord.discount_price_excl_tax)
              "
              class="savings"
            >
              {{
                calculatePercentageDiscount(
                  object,
                  stockRecord.discount_price_excl_tax,
                )
              }}% off
            </td>
            <td
              v-if="!stockRecord.deeper_discount_price_excl_tax"
              class="count"
            >
              {{ stockRecord.discount_threshold }}+ bottles {{ itemSizeStr }}
            </td>
            <td v-else class="count">
              {{ stockRecord.discount_threshold }}-{{
                stockRecord.deeper_discount_threshold - 1
              }}
              {{ getItemTypePlural }}
            </td>
          </tr>
          <tr v-if="stockRecord.deeper_discount_price_excl_tax">
            <td class="price">
              <Price
                :amt="Number(stockRecord.deeper_discount_price_excl_tax)"
              />
            </td>
            <td
              v-show="
                !isTinyDiscount(
                  object,
                  stockRecord.deeper_discount_price_excl_tax,
                )
              "
              class="savings"
            >
              {{
                calculatePercentageDiscount(
                  object,
                  stockRecord.deeper_discount_price_excl_tax,
                )
              }}% off
            </td>
            <td class="count">
              {{ stockRecord.deeper_discount_threshold }} +
              {{ getItemTypePlural }}
            </td>
          </tr>
        </tbody>
      </table>
    </div>
    <div class="price-container">
      <div
        v-if="object.default_record && !object.default_record.price_excl_tax"
        class="row"
      >
        <div class="small-12 columns price">
          <h2 class="price original-price"></h2>
          <span class="item-type-text"> /{{ getItemType }} </span>
        </div>
      </div>
      <div v-if="allowAddToCart" class="add-to-cart-options">
        <div v-if="available" class="quantity-group">
          <button
            class="quantity-change decrease"
            @click="incrementQuantity(-1)"
          >
            <span class="show-for-sr">Decrease</span>
          </button>
          <QuantityWidget
            :quantity="quantity"
            type="text"
            :available="available"
            @clear-errors="clearErrors()"
            @set-quantity="(qty) => (quantity = qty)"
          />
          <button
            class="quantity-change increase"
            @click="incrementQuantity(1)"
          >
            <span class="show-for-sr">Increase</span>
          </button>
        </div>
        <div class="add-to-cart">
          <div v-if="available" class="notify-actions-row show-for-large-up">
            <button @click="addToCart">
              <template v-if="!loading">Add to Cart</template>
              <spinner v-else></spinner>
            </button>
            <HeartIcon
              v-if="
                showHeartIcon && !object.is_sampler && !object.is_ninja_instance
              "
              :product="object"
            />
          </div>
          <div v-else-if="object.show_notify_me">
            <div class="notify-actions-row show-for-large-up">
              <button
                class="notify-me"
                :class="notificationChecked ? 'checked' : ''"
                @click="
                  !$auth.loggedIn
                    ? $auth.openAuthModal()
                    : (notifyMeModalOpen = true)
                "
              >
                <span class="icon-bell"></span>
                <span>{{ notificationChecked ? 'Saved' : 'Notify Me' }}</span>
              </button>
              <HeartIcon :product="object" />
            </div>
          </div>
          <button v-else disabled>Sold Out</button>
          <NotifyMeModal
            :visible="notifyMeModalOpen"
            :product-id="object.product_id"
            @closed="notifyMeModalOpen = false"
          />
        </div>
      </div>
      <button
        v-if="!available && notificationChecked && object.show_notify_me"
        class="disable disable-notifyme"
        @click="disableNotification()"
      >
        Disable notification
      </button>
    </div>
    <ErrorList :errors="errors" />
  </div>
</template>

<script>
import {
  calculatePercentageDiscount,
  disableNotificationService,
  incrementQuantityWidget,
  isAvailable,
  isTinyDiscount,
  requestedNotification,
} from '#common/utils/catalog'
import { baseURL } from '#common/utils/url'

export default {
  name: 'PricingTiers',
  components: {
    Spinner: defineAsyncComponent(
      () => import('#common/components/Loading/Spinner'),
    ),
    Price: defineAsyncComponent(() => import('#common/components/Price.vue')),
    RetailPrice: defineAsyncComponent(
      () => import('@/components/Product/Card/RetailPrice'),
    ),
    QuantityWidget: defineAsyncComponent(
      () => import('@/components/Product/QuantityWidget'),
    ),
    NotifyMeModal: defineAsyncComponent(
      () => import('#common/components/NotifyMe/NotifyMeModal'),
    ),
    ErrorList: defineAsyncComponent(
      () => import('#common/components/ErrorList.vue'),
    ),
    HeartIcon: defineAsyncComponent(
      () => import('@/components/UserList/HeartIcon.vue'),
    ),
  },
  props: {
    stockRecord: {
      type: Object,
      required: true,
    },
    object: {
      type: Object,
      required: true,
    },
    allowAddToCart: {
      type: Boolean,
      default: true,
    },
    showHeartIcon: {
      type: Boolean,
      default: true,
    },
  },
  emits: ['error'],
  data() {
    return {
      quantity: 1,
      notifyMeModalOpen: false,
      errors: [],
      loading: false,
    }
  },
  computed: {
    userEmail() {
      if (this.$auth.loggedIn) {
        return this.$auth.user.email
      } else {
        return ''
      }
    },
    adjustedQuantity() {
      return this.calculateDisplayPrice(this.quantity)
    },
    adjustedBottles() {
      return this.calculateDisplayBottles(this.quantity)
    },
    available() {
      return isAvailable(
        this.$auth.user,
        this.object,
        null,
        this.$config.public.validSiteIds,
      )
    },
    notificationChecked() {
      return requestedNotification(this.$auth.user, this.object.product_id)
    },
    itemSizeStr() {
      if (!this.object.size_str || this.object.size_str === '750ml') {
        return ''
      } else {
        return `(${this.object.size_str})`
      }
    },
    getItemTypeBase() {
      if (this.object.is_sampler) {
        if (this.stockRecord.is_solid_case) {
          return 'case'
        } else {
          return 'set'
        }
      } else {
        return 'bottle'
      }
    },
    getItemType() {
      return `${this.getItemTypeBase} ${this.itemSizeStr}`
    },
    getItemTypePlural() {
      return `${this.getItemTypeBase}s ${this.itemSizeStr}`
    },
  },
  methods: {
    async disableNotification() {
      await disableNotificationService(
        this.$axios,
        this.object.product_id,
        this.$auth.user,
      )
      const userListStore = useUserList()
      await userListStore.fetchProductStatusImmediately(this.object.product_id)
    },
    clearErrors() {
      this.$emit('error', [])
    },
    setNotifyMeModalOpen() {
      this.notifyMeModalOpen = true
    },
    incrementQuantity(amount) {
      this.clearErrors()
      this.quantity = incrementQuantityWidget(amount, this.quantity)
    },
    isTinyDiscount,
    calculatePercentageDiscount,
    calculateDisplayPrice(qty) {
      // Total of 3 Tiers and 2 Thresholds
      // No Tiers
      if (!this.stockRecord.discount_threshold) {
        return this.stockRecord.price_excl_tax
      }
      // One or Two or Three Tiers and qty below threshold 1
      else if (
        this.stockRecord.discount_threshold &&
        qty < this.stockRecord.discount_threshold
      ) {
        return this.stockRecord.price_excl_tax
      }
      // Two Tiers and qty above threshold 1
      else if (
        this.stockRecord.discount_threshold &&
        qty >= this.stockRecord.discount_threshold &&
        !this.stockRecord.deeper_discount_threshold
      ) {
        return this.stockRecord.discount_price_excl_tax
      }
      // Three Tiers and qty above threshold 1 but below threshold 2
      else if (
        this.stockRecord.deeper_discount_threshold &&
        qty >= this.stockRecord.discount_threshold &&
        qty < this.stockRecord.deeper_discount_threshold
      ) {
        return this.stockRecord.discount_price_excl_tax
      }
      // Three Tiers and qty above threshold 2
      else if (
        this.stockRecord.deeper_discount_threshold &&
        qty >= this.stockRecord.deeper_discount_threshold
      ) {
        return this.stockRecord.deeper_discount_price_excl_tax
      }
    },
    calculateDisplayBottles(qty) {
      // Total of 3 Tiers and 2 Thresholds
      // No Tiers
      if (!this.stockRecord.discount_threshold) {
        return ''
      }
      // Two or Three Tiers and qty below threshold 1
      else if (
        this.stockRecord.discount_threshold &&
        qty < this.stockRecord.discount_threshold
      ) {
        return `1-${(this.stockRecord.discount_threshold - 1).toString()} ${
          this.getItemTypePlural
        }`
      }
      // Two Tiers and qty above threshold 1
      else if (
        this.stockRecord.discount_threshold &&
        qty >= this.stockRecord.discount_threshold &&
        !this.stockRecord.deeper_discount_threshold
      ) {
        return `${this.stockRecord.discount_threshold.toString()}+ ${
          this.getItemTypePlural
        }`
      }
      // Three Tiers and qty above threshold 1 but below threshold 2
      else if (
        this.stockRecord.deeper_discount_threshold &&
        qty >= this.stockRecord.discount_threshold &&
        qty < this.stockRecord.deeper_discount_threshold
      ) {
        return (
          `${this.stockRecord.discount_threshold.toString()}-${(
            this.stockRecord.deeper_discount_threshold - 1
          ).toString()} ${this.getItemTypePlural}` +
          '-' +
          (this.stockRecord.deeper_discount_threshold - 1).toString() +
          ' bottles' +
          this.itemSizeStr
        )
      }
      // Three Tiers and qty above threshold 2
      else if (
        this.stockRecord.deeper_discount_threshold &&
        qty >= this.stockRecord.deeper_discount_threshold
      ) {
        return (
          this.stockRecord.deeper_discount_threshold.toString() + '+ bottles'
        )
      }
    },
    async addToCart() {
      this.errors = []
      this.loading = true
      try {
        let route = baseURL() + this.$router.currentRoute.fullPath
        // Use the previous route where we came from if it's there
        if (this.$parent.previousRoute) {
          route = baseURL() + this.$parent.previousRoute.fullPath
        }

        await this.$store.dispatch('addToCart', {
          quantity: this.quantity,
          product_id: this.object.product_id,
          source_url: route,
        })
      } catch (e) {
        console.error(e)
        this.errors = [e.reason || 'Could not add wine to cart']
      }
      this.loading = false
    },
  },
}
</script>
